import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { DataLayer } from '../../../models/data-layer';
import { DataLayerType } from '../../../models/data-layer-type';
import { DataLayerTypeIconComponent } from '../data-layer-type-icon/data-layer-type-icon.component';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { IndicatorData } from '../../../models/indicator-data';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-fast-switch-data-layer',
  standalone: true,
  imports: [DataLayerTypeIconComponent, MenuModule, TieredMenuModule, NgClass],
  templateUrl: './fast-switch-data-layer.component.html',
  styleUrl: './fast-switch-data-layer.component.scss',
})
export class FastSwitchDataLayerComponent implements OnInit, OnChanges {
  readonly layerStore = inject(DataLayerStore);
  readonly typeStore = inject(DataLayerTypeStore);
  readonly geoStore = inject(GeoDataStore);
  readonly dataStore = inject(IndicatorDataStore);

  @Input() readonly = false;
  @Input() dataLayerId!: string;
  @Input() indicatorIds: string[] = [];
  @Input() activeGroup = '';
  @Output() changeLayer = new EventEmitter<DataLayer>();
  @Output() changeGroup = new EventEmitter<{
    group: string;
    dataLayerId: string;
  }>();

  availableDataByIndicators?: Record<string, Record<string, IndicatorData>>;
  dataLayer?: DataLayer;
  dataLayerType?: DataLayerType;
  dataLayerMenu: MenuItem[] = [];
  groupMenu: MenuItem | null = null;

  ngOnInit() {
    this.init();
  }

  /**
   * Responds to changes in the input properties of the component.
   * It is called whenever Angular detects changes to any of the `@Input()` bound properties.
   *
   * @param {SimpleChanges} changes - An object of the changed input properties, where the key is the property name
   * and the value is a `SimpleChange` object detailing the previous and current value.
   * @return {void} This method does not return a value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['indicatorIds'] ||
      changes['dataLayerId'] ||
      changes['activeGroup']
    ) {
      this.init();
    }
  }

  /**
   * Initializes the component by retrieving the necessary data and setting up the required menu configurations.
   *
   * @return {void} This method does not return any value.
   */
  init(): void {
    this.dataLayer = this.layerStore.getById(this.dataLayerId);

    if (this.dataLayer) {
      this.dataLayerType = this.typeStore.getById(this.dataLayer.parent);
      if (this.dataLayer.geoData) {
        this.groupMenu = this.geoStore.getGeoGroupMenu(
          this.dataLayer.geoData,
          this.dataLayer._id,
          true,
          (event: { group: string; dataLayerId: string }) => {
            this.activeGroup = event.group;
            this.changeGroup.emit(event);
          }
        );
      }
    }

    this.indicatorIds && this.indicatorIds.length
      ? this.setDataDrivenMenu()
      : this.setTypeDrivenMenu();
  }

  /**
   * Sets up a type-driven menu by organizing and adding layers grouped by their types to the menu.
   * It initializes the menu, groups layers by their types, and creates menu items for each type and its corresponding layers.
   * If a group menu exists, it is added to the beginning of the menu.
   *
   * @return {void} This method does not return any value.
   */
  setTypeDrivenMenu() {
    this.dataLayerMenu = [];

    const groupedLayer = this.layerStore.groupedByType();
    if (this.groupMenu) {
      this.dataLayerMenu.push(this.groupMenu);
      this.dataLayerMenu.push({ separator: true });
    }

    for (const typeId in groupedLayer) {
      const layers = groupedLayer[typeId];
      const type = this.typeStore.getById(typeId);
      const sortedLayers = this.layerStore.getSortedByIds(
        layers.map(l => l._id)
      );
      this.createMenuItems(type, sortedLayers);
    }
  }

  /**
   * Configures and sets up the data-driven menu by grouping available data based on specified indicators,
   * appending group information and layer types to the menu structure, and generating corresponding menu items.
   *
   * @return {void} Does not return a value.
   */
  setDataDrivenMenu() {
    const availableData = this.dataStore.getGroupedByIndicators(
      this.indicatorIds
    );
    this.dataLayerMenu = [];

    if (this.groupMenu) {
      this.dataLayerMenu.push(this.groupMenu);
      this.dataLayerMenu.push({ separator: true });
    }

    for (const typeId in availableData) {
      const typeDataGroupedByLayerId = availableData[typeId];
      const type = this.typeStore.getById(typeId);

      const sortedLayers = this.layerStore.getSortedByIds(
        Object.keys(typeDataGroupedByLayerId)
      );
      if (type) this.createMenuItems(type, sortedLayers);
    }
  }

  /**
   * Creates and adds menu items for a given data layer type and its associated layers
   * to the dataLayerMenu array.
   *
   * @param {DataLayerType} type - The type of the data layer, containing information like name and type.
   * @param {DataLayer[]} layers - The array of data layers associated with the given type.
   * @return {void} - This method does not return a value.
   */
  createMenuItems(type: DataLayerType, layers: DataLayer[]) {
    const typeLayerItems: MenuItem[] = layers.map(layer => {
      return {
        icon: 'fas fa-calendar-days',
        label: layer.year + '',
        id: layer._id,
        command: (event: MenuItemCommandEvent) => {
          this.changeLayer.emit(layer);
        },
      };
    });

    this.dataLayerMenu.push({
      icon: type.type === 'Point' ? 'fas fa-location-dot' : 'fa-regular fa-map',
      label: type.name,
      items: typeLayerItems,
    });
  }
}
